import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import PropTypes from 'prop-types';
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Checkbox,
    Container,
    Grid,
    MenuItem,
    IconButton,
    Paper,
    TextField,
    Select,
    Snackbar,
    Typography,
} from '@mui/material';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import ModeEditRoundedIcon from '@mui/icons-material/ModeEditRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';

import config from '../../../config';
import {
    ProfileName,
    Pronouns,
    Age,
} from '../../../components/profile/Profile.style';
import { Col, Row } from '../../../styled_components/grid/Grid.style';
import {
  AboutMe,
  AboutMeTextArea,
  ImageContainer,
  ProfileFields,
  ProfileHeading,
  ProfileInfo,
  ProfileInfoField,
  ProfileValue,
  ReadMoreText,
  RightSectionDiv,
  FieldRow,
  ProfileLeft,
  ProfileEditIcon,
  ProfilePicSaveIcon,
  ProfilePicCancelIcon,
} from './edit-profile.style';
import Edit from '../../../assets/images/Edit.svg';
import Tick from '../../../assets/images/Tick.png';
import Close from '../../../assets/images/close.svg';
import { patch } from '../../../services/api.services';
import { API, ACCOUNT_TYPES } from '../../../constants';
import { SET_USER } from '../../../store/actions';
import { ROLES } from '../../../utils/roles';
import { MINORITY_GROUPS, PARENT_CATEGORIES, PARENT_SPECIAL_INTEREST_GROUPS } from '../../../utils/groups';
import { LANGUAGES } from '../../../utils/languages';
import { removeBlankEntries, calculateAge } from '../../../utils/profile';
import Chip from '../../../components/common/chip';
import { uploadToS3 } from '../../../utils/upload';
import { stringToColor } from '../../../utils/stringHelpers';

function EditProfile({ data, getProfile }) {
  const dispatcher = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.account.user);
  const token = useSelector((state) => state.account.token);

  const [enabled, setEnabled] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [readMore, setReadMore] = useState(false);
  const [copied, setCopied] = useState(false);

  const [firstName, setFirstName] = useState(user.profile.first_name);
  const [lastName, setLastName] = useState(user.profile.last_name);
  const [email, setEmail] = useState(user.email || user.username);
  const [phone, setPhone] = useState(user.profile.phone);
  const [bio, setBio] = useState(user.profile.bio);
  const [pronouns, setPronouns] = useState(user.profile.pronouns);
  const [profileImageKey, setProfileImageKey] = useState(user.profile.profile_image_key);
  const [birthDate, setBirthDate] = useState(user.profile.birth_date);
  const [zipCode, setZipCode] = useState(user.profile.zip_code);
  const [inPersonRadius, setInPersonRadius] = useState(user.profile.in_person_radius);
  const [offersVirtualServices, setOffersVirtualServices] = useState(user.profile.offers_virtual_services);
  const [offersInPersonServices, setOffersInPersonServices] = useState(user.profile.offers_in_person_services);
  const [roles, setRoles] = useState(user.profile.roles?.split(',') || []);
  const [clientsSayIAm, setClientsSayIAm] = useState(user.profile.clients_say_i_am?.split(',') || []);
  const [minorityIdentification, setMinorityIdentification] = useState(user.profile.minority_identification?.split(',') || []);
  const [languagesSpoken, setLanguagesSpoken] = useState(user.profile.languages_spoken?.split(',') || []);
  const [parentCategories, setParentCategories] = useState(user.profile.parent_categories?.split(',') || []);
  const [parentSpecialInterests, setParentSpecialInterests] = useState(user.profile.parent_special_interest_groups?.split(',') || []);
  const [address, setAddress] = useState(user.profile.address);
  const [errors, setErrors] = useState([]);
  const [disableSave, setDisableSave] = useState(false);

  const handleClose = (section) => {
    setErrors([]);
    if (section === 'profileInfo') {
      // reset state
      setFirstName(user.profile.first_name);
      setLastName(user.profile.last_name);
      setEmail(user.email);
      setPhone(user.profile.phone);
      setPronouns(user.profile.pronouns);
      setAddress(user.profile.address);
      setBirthDate(user.profile.birth_date);
    } else if (section === 'aboutMe') {
      setBio(user.profile.bio);
    } else if (section === 'trainings') {
      setRoles(user.profile.roles?.split(',') || []);
    } else if (section === 'clientsSayIAm') {
      setClientsSayIAm(user.profile.clients_say_i_am?.split(',') || []);
    } else if (section === 'minorityIdentification') {
      setMinorityIdentification(user.profile.minority_identification?.split(',') || []);
    } else if (section === 'languages') {
      setLanguagesSpoken(user.profile.languages_spoken?.split(',') || []);
    } else if (section === 'profilePic') {
      setProfileImageKey(user.profile.profile_image_key);
    } else if (section === 'parentCategories') {
      setParentCategories(user.profile.parent_categories?.split(',') || []);
    } else if (section === 'parentInterests') {
      setParentSpecialInterests(user.profile.parent_special_interest_groups?.split(',') || []);
    }
  };

  const handleMaxDate = () => {
    const currentDate = new Date();
    let month = currentDate.getMonth() + 1;
    let day = currentDate.getDate();
    const year = currentDate.getFullYear();
    if (month < 10) month = `0${month.toString()}`;
    if (day < 10) day = `0${day.toString()}`;
    const maxDate = `${year}-${month}-${day}`;
    setMaxDate(maxDate);
  };

  const onNewImg = async (acceptedFiles) => {
    if (acceptedFiles.length) {
      setErrors([]);
      setDisableSave(true);

      const path = `profile-pictures/${user.profile.id}/${acceptedFiles[acceptedFiles.length - 1].path}`;
      const successfulUpload = await uploadToS3(
          token,
          path,
          acceptedFiles[acceptedFiles.length - 1],
      );

      if (successfulUpload) {
          setProfileImageKey(path);
          setDisableSave(false);
      }
      else {
        setDisableSave(false);
        setErrors(['We were unable to process your file. Please try again.']);
      }
    }
  }

  const {acceptedFiles, fileRejections, getRootProps, getInputProps, isDragActive} = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/gif': [],
    },
    maxFiles: 1,
    multiple: false,
    maxSize: 5 * 1000 * 1000,
    onDrop: onNewImg,
  });

  const handleSave = async () => {
    const profileResult = await patch(
      `${API.PROFILE}${user.profile.id}/`,
      {
        first_name: firstName,
        last_name: lastName,
        phone,
        bio,
        pronouns,
        address,
        profile_image_key: profileImageKey,
        birth_date: birthDate,
        zip_code: zipCode,
        in_person_radius: inPersonRadius,
        offers_virtual_services: offersVirtualServices,
        offers_in_person_services: offersInPersonServices,
        roles: removeBlankEntries(roles).join(','),
        clients_say_i_am: removeBlankEntries(clientsSayIAm).join(','),
        minority_identification: removeBlankEntries(minorityIdentification).join(','),
        languages_spoken: removeBlankEntries(languagesSpoken).join(','),
        parent_categories: removeBlankEntries(parentCategories).join(','),
        parent_special_interest_groups: removeBlankEntries(parentSpecialInterests).join(','),
      },
      token,
    );

    const userResult = await patch(
      `${API.USER}${user.id}/`,
      {
        username: email,
        email: email,
      },
      token,
    );

    if (userResult.status === 403 || userResult.status === 401 || profileResult.status === 403 || profileResult.status === 401) {
      navigate('/login/experts');
    }
    else if (userResult.status === 200 && profileResult.status === 200) {
      setEnabled('');
      setErrors([]);
      dispatcher({
        type: SET_USER,
        payload: {
          user: {
            ...userResult.data,
            profile: {
              ...profileResult.data,
            },
          },
        },
      });
    }
    else {
      let errMsgs = [];

      if (userResult.status !== 200) {
        for (let key in userResult.data) {
          if (key === 'username') {
            errMsgs = errMsgs.concat(userResult.data[key].map(e => e.replace('username', 'email')));
          } else {
            errMsgs = errMsgs.concat(userResult.data[key]);
          }
        }
      }
      
      if (profileResult.status !== 200) {
        for (let key in profileResult.data) {
          errMsgs = errMsgs.concat(profileResult.data[key]);
        }
      }

      setErrors(errMsgs);
    }
  };

  useEffect(() => {
    handleMaxDate();
  }, [maxDate]);

  return (
    <Box mt={2}>
      <Paper elevation={0}>
        <Box p={4}>
          <Grid container spacing={2}>
            {user.profile.profile_type === ACCOUNT_TYPES.EXPERT && (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" mt={-2} mb={-1}>
                  <Button size="small" variant="text" onClick={() => window.open(`/experts/${user.profile.display_id}`)}>
                    <OpenInNewIcon /> <Box ml={1} display="inline">View Profile</Box>
                  </Button>
                    <Box ml={2}>
                      <CopyToClipboard
                          text={`${config.FE_BASE}/experts/${user.profile.display_id}/`}
                          onCopy={() => setCopied(true)}
                      >
                          <Button size="small" onClick={() => {}} variant="text">
                            <LinkIcon /> <Box ml={1} display="inline">Share</Box>
                          </Button>
                      </CopyToClipboard>
                      <Snackbar
                          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                          open={copied}
                          autoHideDuration={4000}
                          onClose={() => setCopied(false)}
                          message="Share link has been copied to your clipboard"
                      />
                    </Box>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <ProfileLeft>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box>
                    <div {...getRootProps({ style: {} })}>
                        <input {...getInputProps()} />
                        <Box sx={{ height: 75, cursor: 'pointer', position: 'relative' }}>
                          <Avatar
                            src={(acceptedFiles.length && enabled === 'profilePic') ? URL.createObjectURL(acceptedFiles[acceptedFiles.length - 1]) : user.profile.profile_image_url}
                            alt={user.profile.first_name}
                            sx={{ width: 75, height: 75, bgcolor: stringToColor(user.profile.first_name) }}
                            onClick={() => setEnabled('profilePic')}
                          />
                          {enabled !== 'profilePic' && (
                            <Box mt={-3} ml={6}>
                              <IconButton color="primary" size="small" onClick={() => setEnabled('profilePic')} style={{ background: '#FFFFFF' }}>
                                <ModeEditRoundedIcon fontSize="xsmall" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                    </div>
                    {enabled === 'profilePic' && (
                      <Box display="flex" justifyContent="space-between">
                        <Box mt={-3}>
                          <IconButton disabled={disableSave} color="error" size="small" onClick={() => { setEnabled(''); handleClose('profilePic'); }} style={{ background: '#FFFFFF' }}>
                            <ClearRoundedIcon fontSize="xsmall" />
                          </IconButton>
                        </Box>
                        <Box mt={-3}>
                          <IconButton disabled={disableSave} color="success" size="small" onClick={() => handleSave()} style={{ background: '#FFFFFF' }}>
                            <CheckRoundedIcon fontSize="xsmall" />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', ml: 2 }}>
                    <ProfileName>
                      {`${user.profile.first_name} ${user.profile.last_name || ''} `}
                      {user.profile.pronouns && <Pronouns>({user.profile.pronouns})</Pronouns>}
                    </ProfileName>
                    {user.profile.profile_type === ACCOUNT_TYPES.PARENT && (
                      <Age className='fs-exclude'>{user.profile.birth_date ? `${calculateAge(user.profile.birth_date)} years old` : ''}</Age>
                    )}
                    <Age>Joined: {new Date(user.profile.created_at).toLocaleDateString()}</Age>
                  </Box>
                </Box>
              </ProfileLeft>
              <ProfileInfo>
                  {enabled === 'profilePic' && (errors.length > 0 || fileRejections.length > 0) && (
                    <Box mb={2}>
                      {errors.map((error) => (
                        <Alert key={error} variant="outlined" severity="error">{error}</Alert>
                      ))}
                      {fileRejections.map((file) => (
                        <Alert key={file.file.name} variant="outlined" severity="error">{file.errors[0].message}</Alert>
                      ))}
                    </Box>
                  )}
                  <Row>
                    <Col col={6}>
                      <ProfileHeading>
                        Profile Info
                      </ProfileHeading>
                    </Col>
                    <Col col={6}>
                      <ImageContainer>
                        {enabled !== 'profileInfo' ? (
                          <IconButton color="primary" size="small" onClick={() => setEnabled('profileInfo')}>
                            <ModeEditRoundedIcon fontSize="xsmall" />
                          </IconButton>
                        )
                          : (
                            <Box display="flex" justifyContent="space-between">
                              <Box mr={1}>
                                <Button variant="contained" color="secondary" size="small" onClick={() => { setEnabled(''); handleClose('profileInfo'); }}>
                                  <Box mr={1}>Cancel</Box><ClearRoundedIcon fontSize="small" />
                                </Button>
                              </Box>
                              <Button variant="contained" color="primary" size="small" onClick={() => { handleSave(); }}>
                                <Box mr={1}>Save</Box><CheckRoundedIcon fontSize="small" />
                              </Button>
                            </Box>
                          )}
                      </ImageContainer>
                    </Col>
                  </Row>
                  {enabled === 'profileInfo' && errors.length > 0 && (
                    <Row>
                      <Col col={12}>
                        {errors.map((error) => (
                          <Alert key={error} variant="outlined" severity="error">{error}</Alert>
                        ))}
                      </Col>
                    </Row>
                  )}
                  <FieldRow>
                    <Col col={6}>
                      <ProfileFields>
                        First Name
                      </ProfileFields>
                    </Col>
                    <Col col={6}>
                      <ProfileValue enabled={enabled !== 'profileInfo'}>
                        <ProfileInfoField
                          disabled={enabled !== 'profileInfo'}
                          type="text"
                          onChange={(e) => setFirstName(e.target.value )}
                          value={firstName}
                          fontWeight={enabled !== 'profileInfo'}
                          border={enabled !== 'profileInfo'}
                        />
                      </ProfileValue>
                    </Col>
                  </FieldRow>
                  <FieldRow>
                    <Col col={6}>
                      <ProfileFields>
                        Last Name
                      </ProfileFields>
                    </Col>
                    <Col col={6}>
                      <ProfileValue enabled={enabled !== 'profileInfo'}>
                        <ProfileInfoField
                          disabled={enabled !== 'profileInfo'}
                          type="text"
                          onChange={(e) => setLastName(e.target.value)}
                          value={lastName}
                          fontWeight={enabled !== 'profileInfo'}
                          border={enabled !== 'profileInfo'}
                        />
                      </ProfileValue>
                    </Col>
                  </FieldRow>
                  <FieldRow>
                    <Col col={6}>
                      <ProfileFields>
                        Date of Birth
                      </ProfileFields>
                    </Col>
                    <Col col={6}>
                      <ProfileValue enabled={enabled !== 'profileInfo'}>
                        <ProfileInfoField
                          disabled={enabled !== 'profileInfo'}
                          type="date"
                          onChange={(e) => setBirthDate(e.target.value)}
                          value={birthDate}
                          fontWeight={enabled !== 'profileInfo'}
                          border={enabled !== 'profileInfo'}
                          max={maxDate}
                        />
                      </ProfileValue>
                    </Col>
                  </FieldRow>
                  <FieldRow>
                    <Col col={6}>
                      <ProfileFields>
                        Phone Number
                      </ProfileFields>
                    </Col>
                    <Col col={6}>
                      <ProfileValue enabled={enabled !== 'profileInfo'}>
                        <PhoneInput
                          tabIndex="4"
                          placeholder="Eg. 212-456-7890"
                          name="phoneNumber"
                          defaultCountry="US"
                          value={phone}
                          onChange={setPhone}
                          disabled={enabled !== 'profileInfo'}
                          fontWeight={enabled !== 'profileInfo'}
                          border={enabled !== 'profileInfo'}
                          inputComponent={ProfileInfoField}
                          displayInitialValueAsLocalNumber={true}
                        />
                      </ProfileValue>
                    </Col>
                  </FieldRow>
                  <FieldRow>
                    <Col col={6}>
                      <ProfileFields>
                        Email Address
                      </ProfileFields>
                    </Col>
                    <Col col={6}>
                      <ProfileValue enabled={enabled !== 'profileInfo'}>
                        <ProfileInfoField
                          disabled={enabled !== 'profileInfo'}
                          type="email"
                          onChange={(e) => setEmail(e.target.value)}
                          value={email}
                          fontWeight={enabled !== 'profileInfo'}
                          border={enabled !== 'profileInfo'}
                        />
                      </ProfileValue>
                    </Col>
                  </FieldRow>
                  <FieldRow>
                    <Col col={6}>
                      <ProfileFields>
                        Pronouns
                      </ProfileFields>
                    </Col>
                    <Col col={6}>
                      <ProfileValue enabled={enabled !== 'profileInfo'}>
                        <ProfileInfoField
                          disabled={enabled !== 'profileInfo'}
                          type="text"
                          onChange={(e) => setPronouns(e.target.value)}
                          value={pronouns}
                          fontWeight={enabled !== 'profileInfo'}
                          border={enabled !== 'profileInfo'}
                        />
                      </ProfileValue>
                    </Col>
                  </FieldRow>
                  {user.profile.profile_type === ACCOUNT_TYPES.EXPERT && (
                      <>
                          <FieldRow>
                              <Col col={6}>
                                  <ProfileFields>
                                  Virtual Services
                                  </ProfileFields>
                              </Col>
                              <Col col={6}>
                                  <ProfileValue enabled={enabled !== 'profileInfo'}>
                                  <Checkbox
                                      disabled={enabled !== 'profileInfo'}
                                      checked={offersVirtualServices}
                                      id="virtual"
                                      title="virtual"
                                      onChange={(e) => setOffersVirtualServices(e.target.checked)}
                                  />
                                  </ProfileValue>
                              </Col>
                          </FieldRow>
                          <FieldRow>
                              <Col col={6}>
                                  <ProfileFields>
                                  In-Person Services
                                  </ProfileFields>
                              </Col>
                              <Col col={6}>
                                  <ProfileValue enabled={enabled !== 'profileInfo'}>
                                  <Checkbox
                                      disabled={enabled !== 'profileInfo'}
                                      checked={offersInPersonServices}
                                      id="inPerson"
                                      title="inPerson"
                                      onChange={(e) => setOffersInPersonServices(e.target.checked)}
                                  />
                                  </ProfileValue>
                              </Col>
                          </FieldRow>
                          {offersInPersonServices && (
                              <>
                                  <FieldRow>
                                      <Col col={6}>
                                          <ProfileFields>
                                          Zip Code
                                          </ProfileFields>
                                      </Col>
                                      <Col col={6}>
                                          <ProfileValue enabled={enabled !== 'profileInfo'}>
                                          <ProfileInfoField
                                              disabled={enabled !== 'profileInfo'}
                                              type="text"
                                              onChange={(e) => setZipCode(e.target.value)}
                                              value={zipCode}
                                              fontWeight={enabled !== 'profileInfo'}
                                              border={enabled !== 'profileInfo'}
                                          />
                                          </ProfileValue>
                                      </Col>
                                  </FieldRow>
                                  <FieldRow>
                                      <Col col={6}>
                                          <ProfileFields>
                                          In-Person Travel Radius
                                          </ProfileFields>
                                      </Col>
                                      <Col col={6}>
                                          <ProfileValue enabled={enabled !== 'profileInfo'}>
                                          <Select
                                              disabled={enabled !== 'profileInfo'}
                                              name="radius"
                                              id="radius"
                                              value={inPersonRadius}
                                              onChange={(e) => setInPersonRadius(e.target.value)}
                                              sx={{ width: '150px', height: '35px', fontSize: '13px', borderRadius: 2 }}
                                          >
                                              <MenuItem value={null}>Choose an option</MenuItem>
                                              <MenuItem value={5}>5 miles</MenuItem>
                                              <MenuItem value={10}>10 miles</MenuItem>
                                              <MenuItem value={25}>25 miles</MenuItem>
                                              <MenuItem value={50}>50+ miles</MenuItem>
                                          </Select>
                                          </ProfileValue>
                                      </Col>
                                  </FieldRow>
                                  <FieldRow>
                                      <Col col={6}>
                                          <ProfileFields>
                                            Mailing Address
                                          </ProfileFields>
                                      </Col>
                                      <Col col={6}>
                                        <ProfileValue enabled={enabled !== 'profileInfo'}>
                                          <ProfileInfoField
                                            disabled={enabled !== 'profileInfo'}
                                            type="text"
                                            onChange={(e) => setAddress(e.target.value)}
                                            value={address}
                                            fontWeight={enabled !== 'profileInfo'}
                                            border={enabled !== 'profileInfo'}
                                          />
                                        </ProfileValue>
                                      </Col>
                                  </FieldRow>
                              </>
                          )}
                      </>
                  )}
                </ProfileInfo>
            </Grid>
            <Grid item xs={12} md={6}>
              <RightSectionDiv>
                <Box sx={{ mb: 1}}>
                  <Row>
                    <Col col={6}>
                      <ProfileHeading>About Me</ProfileHeading>
                    </Col>
                    <Col col={6}>
                      <ImageContainer>
                        {enabled !== 'aboutMe' ? (
                          <IconButton color="primary" size="small" onClick={() => setEnabled('aboutMe')}>
                            <ModeEditRoundedIcon fontSize="xsmall" />
                          </IconButton>
                        )
                          : (
                            <Box display="flex" justifyContent="space-between">
                              <Box mr={1}>
                                <Button variant="contained" color="secondary" size="small" onClick={() => { setEnabled(''); handleClose('aboutme'); }}>
                                  <Box mr={1}>Cancel</Box><ClearRoundedIcon fontSize="small" />
                                </Button>
                              </Box>
                              <Button variant="contained" color="primary" size="small" onClick={() => { handleSave(); }}>
                                <Box mr={1}>Save</Box><CheckRoundedIcon fontSize="small" />
                              </Button>
                            </Box>
                          )}
                      </ImageContainer>
                    </Col>
                  </Row>
                  {enabled === 'aboutMe' && errors.length > 0 && (
                    <Row>
                      <Col col={12}>
                        {errors.map((error) => (
                          <Alert key={error} variant="outlined" severity="error">{error}</Alert>
                        ))}
                      </Col>
                    </Row>
                  )}
                  {enabled === 'aboutMe'
                    ? (
                      <AboutMeTextArea
                        disabled={enabled !== 'aboutMe'}
                        rows={7}
                        cols={40}
                        type="text"
                        onChange={(e) => setBio(e.target.value)}
                        value={bio}
                        fontWeight={enabled !== 'aboutMe'}
                        border={enabled !== 'aboutMe'}
                      />
                    )
                    : (
                      <>
                        {!readMore && (
                          <AboutMe>
                            {bio?.slice(0, 380)}
                            {bio?.length > 380 && (
                              <>
                                ...
                                {' '}
                                <ReadMoreText onClick={() => setReadMore(true)}>Read More</ReadMoreText>
                              </>
                            )}
                          </AboutMe>
                        )}
                        {readMore && bio?.length > 380 && (
                          <AboutMe>
                            {bio}
                            {' '}
                            <ReadMoreText onClick={() => setReadMore(false)}>Read Less</ReadMoreText>
                          </AboutMe>
                        )}
                      </>
                    )
                  }
                </Box>
                {user.profile.profile_type === ACCOUNT_TYPES.EXPERT ? (
                  <>
                  <Box sx={{ mb: 2}}>
                    <Row>
                      <Col col={9}>
                        <ProfileHeading>I'm trained as a</ProfileHeading>
                      </Col>
                      <Col col={3}>
                        <ImageContainer>
                          {enabled !== 'trainings' ? (
                            <IconButton color="primary" size="small" onClick={() => setEnabled('trainings')}>
                              <ModeEditRoundedIcon fontSize="xsmall" />
                            </IconButton>
                          )
                            : (
                              <Box display="flex" justifyContent="space-between">
                                <Box mr={1}>
                                  <Button variant="contained" color="secondary" size="small" onClick={() => { setEnabled(''); handleClose('trainings'); }}>
                                    <Box mr={1}>Cancel</Box><ClearRoundedIcon fontSize="small" />
                                  </Button>
                                </Box>
                                <Button variant="contained" color="primary" size="small" onClick={() => { handleSave(); }}>
                                  <Box mr={1}>Save</Box><CheckRoundedIcon fontSize="small" />
                                </Button>
                              </Box>
                            )}
                        </ImageContainer>
                      </Col>
                    </Row>
                    {enabled === 'trainings' && errors.length > 0 && (
                      <Row>
                        <Col col={12}>
                          {errors.map((error) => (
                            <Alert key={error} variant="outlined" severity="error">{error}</Alert>
                          ))}
                        </Col>
                      </Row>
                    )}
                    {enabled === 'trainings'
                      ? (
                        <Autocomplete
                          multiple
                          id="trainings"
                          options={[
                            ...ROLES,
                          ]}
                          value={roles.filter((role) => role.trim() !== '')}
                          freeSolo
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip key={option} color="primaryLight" label={option} tagProps={{ ...getTagProps({ index })}} />
                            ))
                          }
                          sx={{
                            width: '100%',
                            mt: 2,
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Add more"
                            />
                          )}
                          onChange={(event, newValue) => {
                            setRoles(newValue);
                          }}
                        />
                      )
                      : (
                        <Box>
                          {roles.filter((role) => role.trim() !== '').map((adjective) => (
                            <Chip key={adjective} label={adjective} color="primaryLight" />
                          ))}
                        </Box>
                      )
                    }
                  </Box>
                  <Box sx={{ mb: 2}}>
                    <Row>
                      <Col col={9}>
                        <ProfileHeading>Clients say I am</ProfileHeading>
                      </Col>
                      <Col col={3}>
                        <ImageContainer>
                          {enabled !== 'clientsSayIAm' ? (
                            <IconButton color="primary" size="small" onClick={() => setEnabled('clientsSayIAm')}>
                              <ModeEditRoundedIcon fontSize="xsmall" />
                            </IconButton>
                          )
                            : (
                              <Box display="flex" justifyContent="space-between">
                                <Box mr={1}>
                                  <Button variant="contained" color="secondary" size="small" onClick={() => { setEnabled(''); handleClose('clientsSayIAm'); }}>
                                    <Box mr={1}>Cancel</Box><ClearRoundedIcon fontSize="small" />
                                  </Button>
                                </Box>
                                <Button variant="contained" color="primary" size="small" onClick={() => { handleSave(); }}>
                                  <Box mr={1}>Save</Box><CheckRoundedIcon fontSize="small" />
                                </Button>
                              </Box>
                            )}
                        </ImageContainer>
                      </Col>
                    </Row>
                    {enabled === 'clientsSayIAm' && errors.length > 0 && (
                      <Row>
                        <Col col={12}>
                          {errors.map((error) => (
                            <Alert key={error} variant="outlined" severity="error">{error}</Alert>
                          ))}
                        </Col>
                      </Row>
                    )}
                    {enabled === 'clientsSayIAm'
                      ? (
                        <Autocomplete
                          multiple
                          id="clients-say-i-am"
                          options={clientsSayIAm.map((option) => option)}
                          value={clientsSayIAm.filter((val) => val.trim() !== '')}
                          freeSolo
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip key={option} color="primaryLight" label={option} {...getTagProps({ index })} />
                            ))
                          }
                          sx={{
                            width: '100%',
                            mt: 2,
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Add an adjective"
                            />
                          )}
                          onChange={(event, newValue) => {
                            setClientsSayIAm(newValue);
                          }}
                        />
                      )
                      : (
                        <Box>
                          {clientsSayIAm.filter((val) => val.trim() !== '').map((adjective) => (
                            <Chip key={adjective} label={adjective} color="primaryLight" />
                          ))}
                        </Box>
                      )
                    }
                  </Box>
                  <Box sx={{ mb: 2}}>
                    <Row>
                      <Col col={6}>
                        {user.profile.profile_type === ACCOUNT_TYPES.EXPERT ? (
                          <ProfileHeading>Fluent engaging with these groups</ProfileHeading>
                        ) : (
                          <ProfileHeading>Affinity group identification</ProfileHeading>
                        )}
                      </Col>
                      <Col col={6}>
                        <ImageContainer>
                          {enabled !== 'minorityIdentification' ? (
                            <IconButton color="primary" size="small" onClick={() => setEnabled('minorityIdentification')}>
                              <ModeEditRoundedIcon fontSize="xsmall" />
                            </IconButton>
                          )
                            : (
                              <Box display="flex" justifyContent="space-between">
                                <Box mr={1}>
                                  <Button variant="contained" color="secondary" size="small" onClick={() => { setEnabled(''); handleClose('minorityIdentification'); }}>
                                    <Box mr={1}>Cancel</Box><ClearRoundedIcon fontSize="small" />
                                  </Button>
                                </Box>
                                <Button variant="contained" color="primary" size="small" onClick={() => { handleSave(); }}>
                                  <Box mr={1}>Save</Box><CheckRoundedIcon fontSize="small" />
                                </Button>
                              </Box>
                            )}
                        </ImageContainer>
                      </Col>
                    </Row>
                    {enabled === 'minorityIdentification' && errors.length > 0 && (
                      <Row>
                        <Col col={12}>
                          {errors.map((error) => (
                            <Alert key={error} variant="outlined" severity="error">{error}</Alert>
                          ))}
                        </Col>
                      </Row>
                    )}
                    {enabled === 'minorityIdentification'
                      ? (
                        <Autocomplete
                          multiple
                          id="minorityIdentification"
                          options={[
                            ...MINORITY_GROUPS,
                          ]}
                          value={minorityIdentification.filter((val) => val.trim() !== '')}
                          freeSolo
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip key={option} color="primaryLight" label={option} tagProps={{ ...getTagProps({ index })}} />
                            ))
                          }
                          sx={{
                            width: '100%',
                            mt: 2,
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Add more"
                            />
                          )}
                          onChange={(event, newValue) => {
                            setMinorityIdentification(newValue);
                          }}
                        />
                      )
                      : (
                        <Box>
                          {minorityIdentification.filter((val) => val.trim() !== '').map((adjective) => (
                            <Chip key={adjective} label={adjective} color="primaryLight" />
                          ))}
                        </Box>
                      )
                    }
                  </Box>
                  <Box sx={{ mb: 2}}>
                    <Row>
                      <Col col={9}>
                        <ProfileHeading>Languages spoken</ProfileHeading>
                      </Col>
                      <Col col={3}>
                        <ImageContainer>
                          {enabled !== 'languages' ? (
                            <IconButton color="primary" size="small" onClick={() => setEnabled('languages')}>
                              <ModeEditRoundedIcon fontSize="xsmall" />
                            </IconButton>
                          )
                            : (
                              <Box display="flex" justifyContent="space-between">
                                <Box mr={1}>
                                  <Button variant="contained" color="secondary" size="small" onClick={() => { setEnabled(''); handleClose('languages'); }}>
                                    <Box mr={1}>Cancel</Box><ClearRoundedIcon fontSize="small" />
                                  </Button>
                                </Box>
                                <Button variant="contained" color="primary" size="small" onClick={() => { handleSave(); }}>
                                  <Box mr={1}>Save</Box><CheckRoundedIcon fontSize="small" />
                                </Button>
                              </Box>
                            )}
                        </ImageContainer>
                      </Col>
                    </Row>
                    {enabled === 'languages' && errors.length > 0 && (
                      <Row>
                        <Col col={12}>
                          {errors.map((error) => (
                            <Alert key={error} variant="outlined" severity="error">{error}</Alert>
                          ))}
                        </Col>
                      </Row>
                    )}
                    {enabled === 'languages'
                      ? (
                        <Autocomplete
                          multiple
                          id="languages"
                          options={[
                            ...LANGUAGES,
                          ]}
                          value={languagesSpoken.filter((val) => val.trim() !== '')}
                          freeSolo
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip key={option} color="primaryLight" label={option} tagProps={{ ...getTagProps({ index })}} />
                            ))
                          }
                          sx={{
                            width: '100%',
                            mt: 2,
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Add more"
                            />
                          )}
                          onChange={(event, newValue) => {
                            setLanguagesSpoken(newValue);
                          }}
                        />
                      )
                      : (
                        <Box>
                          {languagesSpoken.filter((val) => val.trim() !== '').map((language) => (
                            <Chip key={language} label={language} color="primaryLight" />
                          ))}
                        </Box>
                      )
                    }
                  </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ mb: 2}}>
                      <Row>
                        <Col col={9}>
                          <ProfileHeading>Stage</ProfileHeading>
                        </Col>
                        <Col col={3}>
                          <ImageContainer>
                            {enabled !== 'parentCategories' ? (
                              <IconButton color="primary" size="small" onClick={() => setEnabled('parentCategories')}>
                                <ModeEditRoundedIcon fontSize="xsmall" />
                              </IconButton>
                            )
                              : (
                                <Box display="flex" justifyContent="space-between">
                                  <Box mr={1}>
                                    <Button variant="contained" color="secondary" size="small" onClick={() => { setEnabled(''); handleClose('parentCategories'); }}>
                                      <Box mr={1}>Cancel</Box><ClearRoundedIcon fontSize="small" />
                                    </Button>
                                  </Box>
                                  <Button variant="contained" color="primary" size="small" onClick={() => { handleSave(); }}>
                                    <Box mr={1}>Save</Box><CheckRoundedIcon fontSize="small" />
                                  </Button>
                                </Box>
                              )}
                          </ImageContainer>
                        </Col>
                      </Row>
                      {enabled === 'parentCategories' && errors.length > 0 && (
                        <Row>
                          <Col col={12}>
                            {errors.map((error) => (
                              <Alert key={error} variant="outlined" severity="error">{error}</Alert>
                            ))}
                          </Col>
                        </Row>
                      )}
                      {enabled === 'parentCategories'
                        ? (
                          <Autocomplete
                            id="parentCategories"
                            multiple
                            options={[
                              ...PARENT_CATEGORIES,
                            ]}
                            value={parentCategories.filter((val) => val.trim() !== '')}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip key={option} color="primaryLight" label={option} tagProps={{ ...getTagProps({ index })}} />
                              ))
                            }
                            sx={{
                              width: '100%',
                              mt: 2,
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Add more"
                              />
                            )}
                            onChange={(event, newValue) => {
                              setParentCategories(newValue);
                            }}
                          />
                        )
                        : (
                          <Box>
                            {parentCategories.filter((val) => val.trim() !== '').map((cat) => (
                              <Chip key={cat} label={cat} color="primaryLight" />
                            ))}
                          </Box>
                        )
                      }
                    </Box>
                    <Box sx={{ mb: 2}}>
                      <Row>
                        <Col col={9}>
                          <ProfileHeading>Special interest groups</ProfileHeading>
                        </Col>
                        <Col col={3}>
                          <ImageContainer>
                            {enabled !== 'parentInterests' ? (
                              <IconButton color="primary" size="small" onClick={() => setEnabled('parentInterests')}>
                                <ModeEditRoundedIcon fontSize="xsmall" />
                              </IconButton>
                            )
                              : (
                                <Box display="flex" justifyContent="space-between">
                                  <Box mr={1}>
                                    <Button variant="contained" color="secondary" size="small" onClick={() => { setEnabled(''); handleClose('parentInterests'); }}>
                                      <Box mr={1}>Cancel</Box><ClearRoundedIcon fontSize="small" />
                                    </Button>
                                  </Box>
                                  <Button variant="contained" color="primary" size="small" onClick={() => { handleSave(); }}>
                                    <Box mr={1}>Save</Box><CheckRoundedIcon fontSize="small" />
                                  </Button>
                                </Box>
                              )}
                          </ImageContainer>
                        </Col>
                      </Row>
                      {enabled === 'parentInterests' && errors.length > 0 && (
                        <Row>
                          <Col col={12}>
                            {errors.map((error) => (
                              <Alert key={error} variant="outlined" severity="error">{error}</Alert>
                            ))}
                          </Col>
                        </Row>
                      )}
                      {enabled === 'parentInterests'
                        ? (
                          <Autocomplete
                            multiple
                            id="parentInterests"
                            options={[
                              ...PARENT_SPECIAL_INTEREST_GROUPS,
                            ]}
                            value={parentSpecialInterests.filter((val) => val.trim() !== '')}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  key={option}
                                  color="primaryLight"
                                  label={option}
                                  tagProps={{ ...getTagProps({ index })}}
                                />
                              ))
                            }
                            sx={{
                              width: '100%',
                              mt: 2,
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Add more"
                              />
                            )}
                            onChange={(event, newValue) => {
                              setParentSpecialInterests(newValue);
                            }}
                          />
                        )
                        : (
                          <Box>
                            {parentSpecialInterests.filter((val) => val.trim() !== '').map((interest) => (
                              <Chip key={interest} label={interest} color="primaryLight" />
                            ))}
                          </Box>
                        )
                      }
                    </Box>
                  </>
                )}
              </RightSectionDiv>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}

EditProfile.propTypes = {
  user: PropTypes.object,
};

export default EditProfile;
